import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Building2, User, HelpCircle, CreditCard } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const empresasFaqs = [
  {
    question: "Como criar uma vaga?",
    answer:
      'Para criar uma vaga, acesse sua conta empresarial, clique em "Criar Vaga" e preencha os campos obrigatórios: cargo, modalidade (presencial/remoto/híbrido), tipo de contrato (CLT/PJ/estágio), faixa salarial e descrição.',
  },
  {
    question: "Posso editar uma vaga depois de publicada?",
    answer:
      'Sim. Basta acessar o painel da empresa, abrir a vaga desejada e clicar em "Editar". As mudanças são aplicadas imediatamente.',
  },
  {
    question: "Como acompanho os candidatos inscritos?",
    answer:
      "No painel, você encontra todas as vagas e seus respectivos candidatos, com filtros para organizar e visualizar facilmente as informações.",
  },
  {
    question: "Como adicionar perguntas personalizadas para os candidatos?",
    answer:
      "Durante a criação da vaga, você pode selecionar perguntas pré-definidas ou criar suas próprias perguntas. Elas aparecem obrigatoriamente para o candidato antes de finalizar a candidatura.",
  },
  {
    question: "Posso encerrar ou desativar uma vaga?",
    answer: 'Sim. Na página de edição da vaga, clique em "Pausada" ou "Finalizada" para impedir novas candidaturas.',
  },
  {
    question: "Posso ocultar a faixa salarial?",
    answer: "Sim. A faixa salarial pode ser opcional e definida conforme sua política interna.",
  },
  {
    question: "A plataforma tem limite de vagas ativas?",
    answer: "Depende do plano contratado. Planos superiores permitem mais vagas ativas simultaneamente.",
  },
  {
    question: "Como visualizar estatísticas da vaga?",
    answer:
      "A página da vaga inclui indicadores como número de vagas ativas, avaliações pendentes e o número de candidaturas.",
  },
];

const planosCreditosFaqs = [
  {
    question: "Quais são os planos disponíveis para empresas?",
    answer:
      "Oferecemos o Plano Start (gratuito com 1 vaga por 30 dias) e o Plano Profissional (assinatura mensal com opções de 5 a 200 vagas simultâneas).",
  },
  {
    question: "Como funciona a assinatura do Plano Profissional?",
    answer:
      "O Plano Profissional é cobrado mensalmente de forma recorrente via Pix ou Boleto. Você pode cancelar a qualquer momento.",
  },
  {
    question: "O que são créditos avulsos?",
    answer:
      "Créditos avulsos permitem publicar vagas adicionais sem precisar de uma assinatura. Cada crédito equivale a 1 vaga por 30 dias e tem validade de 90 dias após a compra.",
  },
  {
    question: "Posso comprar créditos mesmo tendo uma assinatura ativa?",
    answer:
      "Sim! Os créditos avulsos são independentes da sua assinatura. Você pode usá-los para publicar vagas extras além do limite do seu plano.",
  },
  {
    question: "Quais formas de pagamento são aceitas?",
    answer: "Aceitamos Pix e Boleto Bancário para todas as compras de planos e créditos.",
  },
  {
    question: "Como acompanho meu saldo de créditos?",
    answer: "Você pode ver seu saldo de créditos disponíveis na página \"Minha Conta\" do painel da empresa.",
  },
  {
    question: "Os créditos expiram?",
    answer: "Sim. Os créditos avulsos têm validade de 90 dias a partir da data de compra.",
  },
  {
    question: "Posso mudar de plano a qualquer momento?",
    answer:
      "Sim. Você pode fazer upgrade ou downgrade do seu plano a qualquer momento. As mudanças entram em vigor no próximo ciclo de faturamento.",
  },
];

const candidatosFaqs = [
  {
    question: "Como me candidatar a uma vaga?",
    answer:
      'Crie uma conta, complete seu perfil e clique em "Candidatar-se" na vaga desejada. Algumas empresas podem solicitar perguntas adicionais.',
  },
  {
    question: "Preciso enviar currículo?",
    answer: 'Não é necessário, apenas cadastrar as informações necessárias e relevantes em "Minha Conta".',
  },
  {
    question: "Onde acompanho minhas candidaturas?",
    answer: "No painel do candidato, você vê a lista com todas as vagas às quais se candidatou e o status de cada uma.",
  },
  {
    question: "Como atualizar meu perfil?",
    answer: 'Acesse a área "Minha Conta" e edite suas informações. As mudanças não alteram candidaturas antigas.',
  },
  {
    question: "As empresas conseguem ver meu telefone e email?",
    answer: "Somente após sua candidatura. Antes disso, seus dados ficam privados.",
  },
  {
    question: "Posso excluir minha conta?",
    answer: "Sim. Você pode excluir sua conta a qualquer momento e solicitar remoção total dos dados conforme a LGPD.",
  },
  {
    question: "A plataforma cobra para se candidatar?",
    answer: "Não. Todas as funcionalidades para candidatos são gratuitas.",
  },
  {
    question: "Posso retirar minha candidatura?",
    answer: 'Sim. Basta acessar a vaga e clicar em "Cancelar candidatura".',
  },
  {
    question: "Como funciona a privacidade das minhas informações?",
    answer: "Seus dados são compartilhados apenas com empresas onde você se candidatou. A plataforma segue a LGPD.",
  },
];

const plataformaFaqs = [
  {
    question: "O que é a JobWayX?",
    answer:
      "É uma plataforma que conecta empresas e candidatos, simplificando o processo de criação de vagas, busca de emprego e gerenciamento de candidaturas.",
  },
  {
    question: "A JobWayX é gratuito?",
    answer: "Para candidatos, sempre. Para empresas, depende do plano escolhido.",
  },
  {
    question: "Como entro em contato com o suporte?",
    answer: "Na página de Contato, você encontra um formulário de contato e o email oficial da plataforma.",
  },
  {
    question: "É seguro usar a plataforma?",
    answer:
      "Sim. Toda comunicação é criptografada e a plataforma segue práticas modernas de segurança e conformidade com a LGPD.",
  },
  {
    question: "Como funciona a LGPD na JobWayX?",
    answer:
      "Empresas e candidatos podem solicitar exclusão, portabilidade e revisão de dados. Os dados são coletados apenas para fins de recrutamento.",
  },
  {
    question: "A plataforma funciona em dispositivos móveis?",
    answer: "Sim. Todo o sistema é responsivo e funciona bem em celulares, tablets e desktops.",
  },
  {
    question: "Quanto tempo meus dados ficam armazenados?",
    answer:
      "Os dados ficam armazenados enquanto a conta estiver ativa. Caso solicite exclusão, todo o conteúdo é removido seguindo os prazos da LGPD.",
  },
  {
    question: "Problemas ao fazer login. O que faço?",
    answer: 'Clique em "Esqueci minha senha" ou entre em contato com o suporte para ajuda adicional.',
  },
  {
    question: "Posso usar a plataforma em mais de uma empresa?",
    answer: "Sim, desde que você possua acessos separados para cada empresa.",
  },
  {
    question: "A plataforma tem sistema de avaliação?",
    answer: "Não avaliamos candidatos ou empresas. O foco é conectar vagas e talentos.",
  },
];

interface FaqSectionProps {
  title: string;
  icon: React.ReactNode;
  faqs: { question: string; answer: string }[];
  sectionId: string;
}

const FaqSection = ({ title, icon, faqs, sectionId }: FaqSectionProps) => (
  <div className="mb-10">
    <div className="flex items-center gap-3 mb-6">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">{icon}</div>
      <h2 className="text-2xl font-display font-bold text-foreground">{title}</h2>
    </div>
    <Accordion type="single" collapsible className="space-y-2">
      {faqs.map((faq, index) => (
        <AccordionItem
          key={`${sectionId}-${index}`}
          value={`${sectionId}-${index}`}
          className="border border-border rounded-lg px-4 bg-card"
        >
          <AccordionTrigger className="text-left font-medium hover:no-underline">{faq.question}</AccordionTrigger>
          <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </div>
);

const FaqPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 py-12 bg-muted/30">
        <div className="container">
          <div className="mobile-container max-w-4xl md:mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">Perguntas Frequentes</h1>
              <p className="text-lg text-muted-foreground">
                Encontre respostas para as dúvidas mais comuns sobre a plataforma
              </p>
            </div>

            <FaqSection
              title="Para Empresas"
              icon={<Building2 className="h-6 w-6 text-primary" />}
              faqs={empresasFaqs}
              sectionId="empresa"
            />

            <FaqSection
              title="Planos e Créditos"
              icon={<CreditCard className="h-6 w-6 text-primary" />}
              faqs={planosCreditosFaqs}
              sectionId="planos"
            />

            <FaqSection
              title="Para Candidatos"
              icon={<User className="h-6 w-6 text-primary" />}
              faqs={candidatosFaqs}
              sectionId="candidato"
            />

            <FaqSection
              title="Sobre a Plataforma"
              icon={<HelpCircle className="h-6 w-6 text-primary" />}
              faqs={plataformaFaqs}
              sectionId="plataforma"
            />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FaqPage;
